import { Alert, Button } from 'antd';
import { Pencil } from 'lucide-react';
import { useState } from 'react';
import { useWorkshopDetails } from '../hooks/use-workshop-details';
import { canEditWorkshop, workshopFormFrom } from '../policies/workshop-form.policy';
import type { WorkOrderView } from '../work-orders.api';
import { WorkshopDescriptions } from './WorkshopDescriptions';
import { WorkshopForm } from './WorkshopForm';

interface WorkshopPanelProps {
  order: WorkOrderView;
  canManage: boolean;
}

/** Where the work is done: in-house, or an external garage picked from the directory. This is where the garage is chosen, not on the raise form, because the workshop is decided after the order is approved. */
export function WorkshopPanel({ order, canManage }: WorkshopPanelProps) {
  const { state, error, saving, save, clearError } = useWorkshopDetails(order);
  const [editing, setEditing] = useState(false);

  if (state.status === 'forbidden') return null;
  if (state.status === 'loading') return <p className="text-muted">Loading workshop…</p>;
  if (state.status === 'error') return <Alert type="error" showIcon title="Couldn't load the workshop details" />;

  const { details } = state;
  if (editing) {
    return (
      <WorkshopForm
        initial={workshopFormFrom(details)}
        error={error}
        saving={saving}
        onSave={async (values) => {
          if (await save(values)) setEditing(false);
        }}
        onCancel={() => {
          clearError();
          setEditing(false);
        }}
      />
    );
  }

  return (
    <section aria-label="Workshop" className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-3">
        <h2 className="m-0 text-base font-semibold">Workshop</h2>
        {canEditWorkshop(order.state, canManage) && (
          <Button icon={<Pencil className="size-4" />} onClick={() => setEditing(true)}>
            {details ? 'Edit workshop' : 'Set workshop'}
          </Button>
        )}
      </div>
      <WorkshopDescriptions details={details} />
    </section>
  );
}
